import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createWidgetSettingsApi, type WidgetSettingsApi } from "./api.js";
import type {
  MerchantWidgetDiagnosticDto,
  StorefrontWidgetConfig,
  WidgetSettingsDto,
} from "./models.js";

export interface WidgetSettingsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface WidgetSettingsService {
  loadWidget(): Promise<WidgetSettingsDto>;
  loadDiagnostics(): Promise<MerchantWidgetDiagnosticDto[]>;
  saveWidget(config: StorefrontWidgetConfig): Promise<WidgetSettingsDto>;
}

export interface WidgetSettingsServiceOptions {
  api?: WidgetSettingsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: WidgetSettingsTelemetry;
}

export function createWidgetSettingsService(
  options: WidgetSettingsServiceOptions,
): WidgetSettingsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    loadWidget() {
      return track(telemetry, "merchant_widget_settings_loaded", () => api.getWidget(), {});
    },
    loadDiagnostics() {
      return track(
        telemetry,
        "merchant_widget_diagnostics_loaded",
        () => api.getWidgetDiagnostics(),
        {},
      );
    },
    saveWidget(config) {
      return track(
        telemetry,
        "merchant_widget_settings_saved",
        () => api.updateWidget(config, idempotencyKeyFactory()),
        {
          enabled: config.enabled,
        },
      );
    },
  };
}

async function track<T>(
  telemetry: WidgetSettingsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): WidgetSettingsApi {
  if (client === undefined) {
    throw new Error("Widget settings service requires an API client.");
  }

  return createWidgetSettingsApi(client);
}

function browserTelemetry(): WidgetSettingsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
